import axios, { AxiosInstance, AxiosError } from 'axios'
import { useAuthStore } from './auth'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api'

let apiClient: AxiosInstance | null = null
let refreshPromise: Promise<string> | null = null

const refreshAccessToken = async (): Promise<string | null> => {
  const { refreshToken, userId, userRole } = useAuthStore.getState()

  if (!refreshToken) {
    useAuthStore.getState().logout()
    window.location.href = '/login'
    return null
  }

  try {
    const response = await axios.post(`${API_URL}/auth/refresh-token`, {
      refreshToken,
    })

    const data = response.data?.data || response.data
    const newAccessToken = data?.accessToken
    const newRefreshToken = data?.refreshToken || refreshToken

    if (!newAccessToken) {
      throw new Error('No access token in refresh response')
    }

    useAuthStore.getState().setAuth(
      newAccessToken,
      newRefreshToken,
      userId || '',
      userRole || ''
    )
    return newAccessToken
  } catch (error) {
    useAuthStore.getState().logout()
    window.location.href = '/login'
    return null
  }
}

export const getApiClient = (): AxiosInstance => {
  if (!apiClient) {
    apiClient = axios.create({
      baseURL: API_URL,
    })

    // Add token to requests
    apiClient.interceptors.request.use((config) => {
      const { token } = useAuthStore.getState()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }

      // Set Content-Type for JSON requests, let axios handle FormData
      if (!(config.data instanceof FormData)) {
        config.headers['Content-Type'] = 'application/json'
      }

      return config
    })

    // Handle errors
    apiClient.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any

        // Only handle 401 for actual auth failures (skip login/auth endpoints)
        const isAuthRoute =
          originalRequest.url?.includes('/auth/login') ||
          originalRequest.url?.includes('/auth/register') ||
          originalRequest.url?.includes('/auth/forgot-password') ||
          originalRequest.url?.includes('/auth/verify-forgot-password-otp') ||
          originalRequest.url?.includes('/auth/reset-password')
        if (error.response?.status === 401 && !originalRequest._retry && !isAuthRoute) {
          originalRequest._retry = true

          // Attempt to refresh token
          if (!refreshPromise) {
            refreshPromise = refreshAccessToken().then((token) => {
              refreshPromise = null
              return token || ''
            })
          }

          const newToken = await refreshPromise
          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`
            return apiClient!(originalRequest)
          }
        }

        // Don't logout for other errors (403, 404, 500, etc.)
        // Just reject the promise and let the component handle it
        return Promise.reject(error)
      }
    )
  }

  return apiClient
}

// API Methods
export const api = {
  // Dashboard (Admin)
  getDashboardOverview: () => getApiClient().get('/admin/dashboard/overview'),
  getFullDashboard: () => getApiClient().get('/admin/dashboard'),
  getOrderStats: () => getApiClient().get('/admin/dashboard/stats'),
  getRecentOrders: (limit = 5) => getApiClient().get(`/admin/dashboard/recent-orders?limit=${limit}`),
  getNewCustomers: (limit = 10) => getApiClient().get(`/admin/dashboard/new-customers?limit=${limit}`),
  getLowStockProducts: (limit = 10) => getApiClient().get(`/admin/dashboard/low-stock-products?limit=${limit}`),
  getProductStats: () => getApiClient().get('/admin/dashboard/product-stats'),
  getCustomerStats: () => getApiClient().get('/admin/dashboard/customer-stats'),
  getReviewStats: () => getApiClient().get('/admin/dashboard/review-stats'),
  getCouponStats: () => getApiClient().get('/admin/dashboard/coupon-stats'),
  getTodayRevenue: () => getApiClient().get('/admin/dashboard/revenue/today'),
  getThisWeekRevenue: () => getApiClient().get('/admin/dashboard/revenue/week'),
  getThisMonthRevenue: () => getApiClient().get('/admin/dashboard/revenue/month'),
  getRevenueTrend: (days = 7) => getApiClient().get(`/admin/dashboard/revenue/trend?days=${days}`),
  getTodayStats: () => getApiClient().get('/admin/dashboard/today-stats'),
  getTopProducts: (limit = 10) => getApiClient().get(`/admin/dashboard/top-products?limit=${limit}`),
  getAnalyticsData: () => getApiClient().get('/admin/dashboard/analytics'),

  // Products (Admin)
  getProducts: (page = 1, limit = 10, search = '', extraFilters: Record<string, any> = {}) => {
    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('limit', String(limit))
    if (search?.trim()) params.set('search', search.trim())
    Object.entries(extraFilters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '' && v !== 'all') {
        params.set(k, String(v))
      }
    })
    return getApiClient().get(`/admin/products?${params.toString()}`)
  },
  getProduct: (id: string) => getApiClient().get(`/admin/products/${id}`),
  createProduct: (data: any) => getApiClient().post('/admin/products', data),
  updateProduct: (id: string, data: any) => getApiClient().put(`/admin/products/${id}`, data),
  deleteProduct: (id: string) => getApiClient().delete(`/admin/products/${id}`),

  // Categories (Admin)
  getCategories: (page = 1, limit = 20, search = '') => {
    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('limit', String(limit))
    if (search?.trim()) params.set('search', search.trim())
    return getApiClient().get(`/admin/categories?${params.toString()}`)
  },
  getCategory: (id: string) => getApiClient().get(`/admin/categories/${id}`),
  createCategory: (data: any) => getApiClient().post('/admin/categories', data),
  updateCategory: (id: string, data: any) => getApiClient().put(`/admin/categories/${id}`, data),
  deleteCategory: (id: string) => getApiClient().delete(`/admin/categories/${id}`),

  // Orders (Admin)
  getOrders: (page = 1, limit = 10, status = '', search = '', extraFilters: Record<string, any> = {}) => {
    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('limit', String(limit))
    if (status) params.set('status', status)
    if (search?.trim()) params.set('search', search.trim())
    Object.entries(extraFilters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '' && v !== 'all') {
        params.set(k, String(v))
      }
    })
    return getApiClient().get(`/admin/orders?${params.toString()}`)
  },
  getOrder: (id: string) => getApiClient().get(`/admin/orders/${id}`),
  updateOrderStatus: (id: string, status: string, note?: string) =>
    getApiClient().put(`/admin/orders/${id}/status`, { status, note }),
  updatePaymentStatus: (id: string, paymentStatus: string) =>
    getApiClient().put(`/admin/orders/${id}/payment-status`, { paymentStatus }),
  updateOrderTracking: (id: string, trackingNumber: string, estimatedDelivery?: string) =>
    getApiClient().put(`/admin/orders/${id}/tracking`, { trackingNumber, estimatedDelivery }),
  refundOrder: (id: string, paymentId: string, amount?: number) =>
    getApiClient().post(`/admin/orders/${id}/refund`, { paymentId, amount }),
  deleteOrder: (id: string) => getApiClient().delete(`/admin/orders/${id}`),
  exportOrders: (status = '') => {
    const params = new URLSearchParams()
    if (status) params.set('status', status)
    return getApiClient().get(`/admin/orders/export?${params.toString()}`, {
      responseType: 'blob',
    })
  },

  // Customers (Admin)
  getCustomers: (page = 1, limit = 10, search = '', extraFilters: Record<string, any> = {}) => {
    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('limit', String(limit))
    if (search?.trim()) params.set('search', search.trim())
    Object.entries(extraFilters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '' && v !== 'all') {
        params.set(k, String(v))
      }
    })
    return getApiClient().get(`/admin/customers?${params.toString()}`)
  },
  getCustomer: (id: string) => getApiClient().get(`/admin/customers/${id}`),
  updateCustomer: (id: string, data: any) => getApiClient().put(`/admin/customers/${id}`, data),
  deleteCustomer: (id: string) => getApiClient().delete(`/admin/customers/${id}`),
  getCustomerOrders: (customerId: string, page = 1, limit = 100, status = '') =>
    getApiClient().get(`/orders/admin/customers/${customerId}?page=${page}&limit=${limit}${status ? `&status=${status}` : ''}`),

  // Reviews (Admin)
  getReviews: (page = 1, limit = 10, status = '', search = '', extraFilters: Record<string, any> = {}) => {
    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('limit', String(limit))
    if (status) params.set('status', status)
    if (search?.trim()) params.set('search', search.trim())
    Object.entries(extraFilters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '' && v !== 'all') {
        params.set(k, String(v))
      }
    })
    return getApiClient().get(`/admin/reviews?${params.toString()}`)
  },
  updateReview: (id: string, data: any) => getApiClient().put(`/admin/reviews/${id}`, data),
  deleteReview: (id: string) => getApiClient().delete(`/admin/reviews/${id}`),
  approveReview: (id: string) => getApiClient().put(`/admin/reviews/${id}/approve`),
  rejectReview: (id: string) => getApiClient().put(`/admin/reviews/${id}/reject`),
  hideReview: (id: string) => getApiClient().put(`/admin/reviews/${id}/hide`),

  // Contact Messages (Admin)
  getContactMessages: (page = 1, limit = 10, search = '', extraFilters: Record<string, any> = {}) => {
    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('limit', String(limit))
    if (search?.trim()) params.set('search', search.trim())
    Object.entries(extraFilters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '' && v !== 'all') {
        params.set(k, String(v))
      }
    })
    return getApiClient().get(`/admin/contact-messages?${params.toString()}`)
  },
  getContactMessage: (id: string) => getApiClient().get(`/admin/contact-messages/${id}`),
  replyToContactMessage: (id: string, reply: string) =>
    getApiClient().post(`/admin/contact-messages/${id}/reply`, { reply }),
  updateContactMessageStatus: (id: string, status: string) =>
    getApiClient().put(`/admin/contact-messages/${id}/status`, { status }),
  deleteContactMessage: (id: string) => getApiClient().delete(`/admin/contact-messages/${id}`),

  // Coupons (Admin)
  getCoupons: (page = 1, limit = 10, search = '', extraFilters: Record<string, any> = {}) => {
    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('limit', String(limit))
    if (search?.trim()) params.set('search', search.trim())
    Object.entries(extraFilters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '' && v !== 'all') {
        params.set(k, String(v))
      }
    })
    return getApiClient().get(`/admin/coupons?${params.toString()}`)
  },
  createCoupon: (data: any) => getApiClient().post('/admin/coupons', data),
  updateCoupon: (id: string, data: any) => getApiClient().put(`/admin/coupons/${id}`, data),
  deleteCoupon: (id: string) => getApiClient().delete(`/admin/coupons/${id}`),

  // Payments (Admin)
  getPayments: (page = 1, limit = 10, search = '', extraFilters: Record<string, any> = {}) => {
    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('limit', String(limit))
    if (search?.trim()) params.set('search', search.trim())
    Object.entries(extraFilters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '' && v !== 'all') {
        params.set(k, String(v))
      }
    })
    return getApiClient().get(`/admin/payments?${params.toString()}`)
  },
  getPayment: (id: string) => getApiClient().get(`/admin/payments/${id}`),
  refundPayment: (paymentId: string, amount?: number) =>
    getApiClient().post(`/admin/payments/${paymentId}/refund`, { paymentId, amount }),
  exportPayments: (search = '', status = '', fromDate = '', toDate = '') => {
    const params = new URLSearchParams()
    if (search?.trim()) params.set('search', search.trim())
    if (status) params.set('status', status)
    if (fromDate) params.set('fromDate', fromDate)
    if (toDate) params.set('toDate', toDate)

    return getApiClient().get(`/admin/payments/export?${params.toString()}`, {
      responseType: 'blob',
    })
  },

  // Auth
  login: (email: string, password: string) =>
    getApiClient().post('/auth/login', { email, password }),
  forgotPassword: (email: string) =>
    getApiClient().post('/auth/forgot-password', { email, requireAdmin: true }),
  verifyForgotPasswordOtp: (email: string, otp: string) =>
    getApiClient().post('/auth/verify-forgot-password-otp', { email, otp, requireAdmin: true }),
  resetPassword: (email: string, otp: string, newPassword: string) =>
    getApiClient().post('/auth/reset-password', { email, otp, newPassword, requireAdmin: true }),

  // Admin Profile & Password
  getAdminProfile: () => getApiClient().get('/admin/profile'),
  updateAdminProfile: (data: any) => getApiClient().put('/admin/profile', data),
  changeAdminPassword: (data: { currentPassword?: string; oldPassword?: string; newPassword: string; confirmPassword: string }) =>
    getApiClient().post('/admin/change-password', data),

  // Shiprocket Logistics (Admin)
  createShipment: (orderId: string) => getApiClient().post(`/shiprocket/admin/create-shipment/${orderId}`),
  generateAwb: (orderId: string) => getApiClient().post(`/shiprocket/admin/generate-awb/${orderId}`),
  assignCourier: (orderId: string) => getApiClient().post(`/shiprocket/admin/assign-courier/${orderId}`),
  schedulePickup: (orderId: string) => getApiClient().post(`/shiprocket/admin/schedule-pickup/${orderId}`),
  cancelShipment: (orderId: string) => getApiClient().post(`/shiprocket/admin/cancel-shipment/${orderId}`),
  retryFulfillment: (orderId: string) => getApiClient().post(`/shiprocket/admin/retry/${orderId}`),
  getLabel: (orderId: string) => getApiClient().get(`/shiprocket/admin/label/${orderId}`),
  getInvoice: (orderId: string) => getApiClient().get(`/shiprocket/admin/invoice/${orderId}`),
  trackOrderAdmin: (orderId: string) => getApiClient().get(`/shiprocket/track/${orderId}`),

  // Legacy method names for backward compatibility
  getAll: (page = 1, limit = 10) => getApiClient().get(`/admin/products?page=${page}&limit=${limit}`),
  getById: (id: string) => getApiClient().get(`/admin/products/${id}`),
  create: (data: any) => getApiClient().post('/admin/products', data),
  update: (id: string, data: any) => getApiClient().put(`/admin/products/${id}`, data),
  delete: (id: string) => getApiClient().delete(`/admin/products/${id}`),
  reply: (id: string, text: string) => getApiClient().post(`/admin/contact-messages/${id}/reply`, { text }),
  updateStatus: (id: string, status: string) => getApiClient().put(`/admin/contact-messages/${id}/status`, { status }),
}
